/**
 * The guided tour: one list of steps. The full tour walks all of them; a
 * screen's tour ("Tour this screen") walks the ones on that screen. Each step
 * points at a region the screen marks with `data-tour` (src/lib/tour.ts),
 * most specific anchor first: a status pill in the first row, else the table
 * that holds it. The copy says what the region is for, and holds whatever
 * state the region is in (features/tour/resolve.ts adds the state's note).
 */
import { SCREENS } from '@/app/screens';
import { SAMPLE_GAME } from '@/lib/sample';

/** The screens the tour visits, by their `SCREENS` key. */
export type TourScreen = 'overview' | 'performance' | 'attributeInsights' | 'creativeLab';

export const TOUR_SCREENS: readonly TourScreen[] = ['overview', 'performance', 'attributeInsights', 'creativeLab'];

export type TourContext = {
  /** The tab is exploring sample data (src/lib/sample.ts). */
  sample: boolean;
  /** The shell is in its mobile layout: the sidebar sits behind the menu button. */
  mobile: boolean;
};

export type TourStep = {
  id: string;
  /** Where the step is shown; null on whichever screen the tour is on (the shell). */
  screen: TourScreen | null;
  /** CSS selectors, most specific first. The first one on screen is highlighted. */
  anchors: readonly string[];
  title: string;
  body: string | ((ctx: TourContext) => string);
  /** The step's own button in place of "Next" (the last sample step). */
  action?: 'connect';
  /** Skip the step unless this holds. */
  when?: (ctx: TourContext) => boolean;
};

const anchor = (name: string) => `[data-tour="${name}"]`;

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'focus-app',
    screen: 'overview',
    anchors: [anchor('focus-app'), anchor('focus-bar')],
    title: 'One app at a time',
    body: 'Every screen is about the app chosen here. The list ranks your apps by recent spend and says whether each has enough data to judge.',
  },
  {
    id: 'focus-scope',
    screen: 'overview',
    anchors: [anchor('focus-bar')],
    title: 'Dates and filters',
    body: 'Lemon picks the shortest window with enough data. Change the dates, or narrow to countries, networks or creative types. The app and window carry over to every screen.',
  },
  {
    id: 'pulse',
    screen: 'overview',
    anchors: [anchor('pulse')],
    title: 'Your headline metrics',
    body: 'Each metric against the period before, with its trend. Where an alert sets a target, it says whether you are meeting it.',
  },
  {
    id: 'findings',
    screen: 'overview',
    anchors: [anchor('findings')],
    title: 'What changed',
    body: 'The changes that matter most, ranked by the money at stake. Each says why it matters and links to the screen where you can act on it.',
  },
  {
    id: 'performers',
    screen: 'overview',
    anchors: [anchor('performers')],
    title: 'Winners and losers',
    body: 'The creatives that moved your key metric most, among those that spent enough to count. Each opens in Performance.',
  },
  {
    id: 'metrics',
    screen: 'performance',
    anchors: [anchor('metrics')],
    title: 'Every creative, measured',
    body: 'The same headline metrics for the creatives below. Each metric’s info button explains how it is calculated.',
  },
  {
    id: 'status',
    screen: 'performance',
    anchors: [`tr ${anchor('status')}`, anchor('creatives')],
    title: 'A status for every creative',
    // Tooltips need hover or focus; on a phone the creative's drawer shows the same evidence.
    body: ({ mobile }) =>
      `Scaling means the network is giving a creative a growing share of its spend; Fatiguing means its install rate is falling. Each status comes from a written rule. ${mobile ? 'Open a creative' : 'Hover over or focus a status'} to see the rule and the numbers behind it.`,
  },
  {
    id: 'board',
    screen: 'performance',
    anchors: [anchor('view-toggle')],
    title: 'Table or board',
    body: 'The board sorts creatives into lanes by what to do next: scale, keep, replace or watch.',
  },
  {
    id: 'creative',
    screen: 'performance',
    // The creative's name cell: a whole row is wider than a phone's screen.
    anchors: ['tr[data-row-kind="asset"] > th', anchor('creatives')],
    title: 'Open any creative',
    body: 'Click a creative to see where it ran, its daily trend and its attributes, and to brief a replacement in Creative lab.',
  },
  {
    id: 'insights',
    screen: 'attributeInsights',
    anchors: [anchor('insights-header'), anchor('focus-bar')],
    title: 'What your best creatives share',
    body: 'Attribute insights compare creatives with and without an attribute (a format, a hook, a colour) and say how much evidence stands behind each difference.',
  },
  {
    id: 'insight-card',
    screen: 'attributeInsights',
    anchors: ['[data-insight-id]', anchor('insights')],
    title: 'Strongest evidence first',
    body: 'Insights are grouped by how certain they are. Open one to compare the two groups of creatives side by side.',
  },
  {
    id: 'ideas',
    screen: 'creativeLab',
    anchors: [anchor('ideas-evidence'), anchor('focus-bar')],
    title: 'Ideas from your own results',
    body: 'Creative lab turns this app’s winning and losing attributes into ideas for new creatives, and shows the evidence behind each.',
  },
  {
    id: 'new-project',
    screen: 'creativeLab',
    anchors: [anchor('new-project')],
    title: 'From idea to creative',
    body: 'Start a project from an idea, or from scratch, to generate images, videos and playable ads.',
  },
  {
    id: 'everything-else',
    screen: null,
    anchors: [anchor('nav-tools'), anchor('nav'), anchor('nav-menu')],
    title: 'Everything else',
    body: ({ mobile }) => {
      const where = mobile ? 'the menu' : 'the sidebar';
      return `The rest is in ${where}: Tools to tag attributes, set alerts, resize images and forecast revenue, and Settings for connections, metric weights, users and billing. Replay this tour from ${where} or search.`;
    },
  },
  {
    id: 'connect',
    screen: null,
    anchors: [anchor('sample-banner')],
    title: 'Now with your own games',
    body: `This was ${SAMPLE_GAME}, a fictional game. Connect your ad networks to see all of it for your own apps.`,
    action: 'connect',
    when: ({ sample }) => sample,
  },
];

export const screenPath = (screen: TourScreen): string => SCREENS[screen].path;

/** The steps a tour walks: all of them, or one screen's, minus those that do not apply. */
export const stepsFor = (scope: TourScreen | 'all', ctx: TourContext): TourStep[] =>
  TOUR_STEPS.filter((step) => (scope === 'all' || step.screen === scope) && (step.when?.(ctx) ?? true));

export const bodyOf = (step: TourStep, ctx: TourContext): string => (typeof step.body === 'function' ? step.body(ctx) : step.body);
